import React, { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { adminRoleService } from '../../services/admin.role.service';
import { errorService } from '../../services/error.service';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import styles from '../../styles/components/CreateRoleModal.module.css';
import modalStyles from '../../styles/common/Modal.module.css';

interface CreateRoleModalProps {
    show: boolean;
    onHide: () => void;
    onSuccess: () => void;
}

const CreateRoleModal: React.FC<CreateRoleModalProps> = ({ show, onHide, onSuccess }) => {
    const [roleName, setRoleName] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!roleName.trim()) {
            await Swal.fire({
                icon: 'warning',
                title: 'Campo Requerido',
                text: 'Por favor, ingresa el nombre del rol.',
                confirmButtonColor: 'var(--color-primary, #1e293b)'
            });
            return;
        }

        try {
            setLoading(true);
            const response = await adminRoleService.createRole({ name: roleName.trim() });

            if (response.statusCode === 200) {
                onHide();
                setRoleName('');
                onSuccess();
                await Swal.fire({
                    icon: 'success',
                    title: '¡Éxito!',
                    text: 'El rol ha sido creado correctamente.',
                    confirmButtonText: 'Aceptar',
                    confirmButtonColor: 'var(--color-primary, #1e293b)'
                });
            } else {
                errorService.showErrorAlert(response, 'Error al Crear Rol');
            }
        } catch (error: any) {
            console.error('Error in CreateRoleModal:', error);
            errorService.showErrorAlert(error, 'Error de Conexión');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            className={styles.createRoleModal}
            contentClassName={styles.scaleIn}
            animation={false}
        >
            <Modal.Header closeButton>
                <Modal.Title as="h3" className={modalStyles.modalTitle}>
                    Crear Nuevo Rol
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className={styles.modalSubtitle}>
                    Ingresa el nombre para el nuevo rol administrativo.
                </p>
                <Form onSubmit={handleSubmit} id="create-role-form" noValidate>
                    <Form.Group className="mb-3">
                        <Form.Label>Nombre del Rol</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Ej: Editor de Contenido"
                            value={roleName}
                            onChange={(e) => setRoleName(e.target.value)}
                            className={styles.createRoleModalInput}
                            autoFocus
                            disabled={loading}
                        />
                        <Form.Text className="text-muted">
                            Asegúrate de que el nombre sea descriptivo y único.
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>

            <Modal.Footer className={styles.modalFooter}>
                <Button 
                    variant="secondary" 
                    onClick={onHide} 
                    className={styles.secondaryBtn}
                    disabled={loading}
                >
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    type="submit" 
                    form="create-role-form"
                    className={modalStyles.closeBtn}
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Creando...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={faSave} className="me-2" /> Guardar
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateRoleModal;
